import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaEnvelope, FaArrowRight, FaLinkedin, FaTwitter } from 'react-icons/fa';

const Section = styled.section`
  padding: 7rem 6%;
  background: linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 5rem 6%;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 100%);
    clip-path: polygon(20% 0, 100% 0, 100% 100%, 0% 100%);
    z-index: 1;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
  color: #555;
  line-height: 1.7;
  position: relative;
  padding-bottom: 2rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #333, #999);
    border-radius: 3px;
  }
`;

const ContactCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 4rem;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const IconCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f2f2f2 0%, #fafafa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  
  svg {
    font-size: 2.5rem;
    color: #333;
  }
`;

const ContactHeading = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 700;
`;

const ContactDescription = styled.p`
  color: #666;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.7;
  max-width: 600px;
`;

const EmailContainer = styled(motion.div)`
  margin: 2rem 0;
  padding: 1.5rem 2.5rem;
  background: linear-gradient(to right, #f7f7f7, #f0f0f0);
  border-radius: 12px;
  display: inline-block;
`;

const EmailLink = styled.a`
  color: #111;
  font-weight: 600;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: #555;
  }
  
  svg {
    margin-right: 0.8rem;
  }
`;

const ActionButton = styled(motion.a)`
  background: linear-gradient(90deg, #333, #444);
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  svg {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: linear-gradient(90deg, #222, #333);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2.5rem;
`;

const SocialLink = styled.a`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background: #f0f0f0;
    color: #111;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const Contact = () => {
  return (
    <Section id="contact">
      <ContentContainer>
        <SectionHeader>
          <SectionTitle>Contact Us</SectionTitle>
          <SectionSubtitle>
            Have questions about our services? Reach out directly to our legal team.
          </SectionSubtitle>
        </SectionHeader>
        
        <ContactCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <IconCircle>
            <FaEnvelope />
          </IconCircle>
          
          <ContactHeading>Get in Touch</ContactHeading>
          
          <ContactDescription>
            Our team of legal experts is ready to assist with your startup's unique challenges. 
            We provide personalized solutions tailored to your business needs and growth objectives.
          </ContactDescription>
          
          <EmailContainer
            whileHover={{ y: -5 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <EmailLink href="mailto:hello@legalhdm.com">
              <FaEnvelope /> hello@legalhdm.com
            </EmailLink>
          </EmailContainer>
          
          <ActionButton 
            href="mailto:hello@legalhdm.com?subject=Legal%20Service%20Inquiry"
            whileHover={{ scale: 1.05 }}
          >
            Send us an email <FaArrowRight />
          </ActionButton>
          
          <SocialLinks>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              <FaLinkedin />
            </SocialLink>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              <FaTwitter />
            </SocialLink>
          </SocialLinks>
        </ContactCard>
      </ContentContainer>
    </Section>
  );
};

export default Contact; 